/**
 * Token Expiration Grace Period Service
 *
 * Provides a grace period after token expiration before requiring authentication.
 * This allows users to continue working without interruption until they attempt
 * an action that requires authentication.
 */

interface GracePeriodState {
  isInGracePeriod: boolean;
  expirationTime: number | null;
  gracePeriodEndTime: number | null;
}

class TokenExpirationGraceService {
  private readonly GRACE_PERIOD_DURATION = 5 * 60 * 1000; // 5 minutes
  private readonly STATE_KEY = 'token_grace_period_state';

  private state: GracePeriodState = {
    isInGracePeriod: false,
    expirationTime: null,
    gracePeriodEndTime: null,
  };

  constructor() {
    this.loadState();
  }

  /**
   * Mark that token has expired and start grace period
   */
  startGracePeriod(expirationTime: number): void {
    const now = Date.now();
    const gracePeriodEndTime = now + this.GRACE_PERIOD_DURATION;

    this.state = {
      isInGracePeriod: true,
      expirationTime,
      gracePeriodEndTime,
    };

    this.saveState();
    console.log('Token expiration grace period started', {
      expirationTime: new Date(expirationTime).toISOString(),
      gracePeriodEndTime: new Date(gracePeriodEndTime).toISOString(),
    });
  }

  /**
   * Check if we're currently in grace period
   */
  isInGracePeriod(): boolean {
    if (!this.state.isInGracePeriod) {
      return false;
    }

    // Check if grace period has expired
    if (
      this.state.gracePeriodEndTime &&
      Date.now() > this.state.gracePeriodEndTime
    ) {
      this.endGracePeriod();
      return false;
    }

    return true;
  }

  /**
   * End the grace period (e.g., after successful token refresh)
   */
  endGracePeriod(): void {
    this.state = {
      isInGracePeriod: false,
      expirationTime: null,
      gracePeriodEndTime: null,
    };

    this.saveState();
    console.log('Token expiration grace period ended');
  }

  /**
   * Check if an action requires immediate authentication
   * Returns true if grace period has expired and user needs to authenticate
   */
  requiresImmediateAuth(): boolean {
    return !this.isInGracePeriod();
  }

  /**
   * Get remaining grace period time in milliseconds
   */
  getRemainingGraceTime(): number {
    if (!this.state.isInGracePeriod || !this.state.gracePeriodEndTime) {
      return 0;
    }

    const remaining = this.state.gracePeriodEndTime - Date.now();
    return Math.max(0, remaining);
  }

  /**
   * Save state to localStorage
   */
  private saveState(): void {
    try {
      localStorage.setItem(this.STATE_KEY, JSON.stringify(this.state));
    } catch (error) {
      console.error('Failed to save grace period state:', error);
    }
  }

  /**
   * Load state from localStorage
   */
  private loadState(): void {
    try {
      const saved = localStorage.getItem(this.STATE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validate grace period hasn't expired
        if (
          parsed.gracePeriodEndTime &&
          Date.now() > parsed.gracePeriodEndTime
        ) {
          this.endGracePeriod();
        } else {
          this.state = parsed;
        }
      }
    } catch (error) {
      console.error('Failed to load grace period state:', error);
      this.endGracePeriod();
    }
  }

  /**
   * Clear all state (e.g., on logout)
   */
  clear(): void {
    this.endGracePeriod();
    try {
      localStorage.removeItem(this.STATE_KEY);
    } catch (error) {
      console.error('Failed to clear grace period state:', error);
    }
  }
}

// Export singleton instance
export const tokenExpirationGraceService = new TokenExpirationGraceService();
export default tokenExpirationGraceService;
